import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SceneLayout from '@/components/scene/SceneLayout';
import Aperture from '@/components/scene/Aperture';
import Poche from '@/components/scene/Poche';
import heroFacade from '@/assets/scene/home-hero-facade.webp';
import stillStair from '@/assets/scene/home-still-stair.webp';
import roomWriting from '@/assets/scene/home-room-writing.webp';
import roomChannels from '@/assets/scene/home-room-channels.webp';
import roomAnalytics from '@/assets/scene/home-room-analytics.webp';
import transom from '@/assets/scene/home-transom.webp';
import { useActiveIndex } from '@/components/scene/useActiveIndex';
import { stepOf } from '@/components/scene/format';

const SCHEDULE = ['wa', 'sms', 'email', 'ai', 'crm', 'analytics'] as const;

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { active, register } = useActiveIndex<HTMLElement>();

  const rooms = [
    {
      title: t('home.room1_title'),
      desc: t('home.room1_desc'),
      fig: t('home.fig_room1'),
      img: roomWriting,
      alt: t('home.room1_alt'),
      fragment: (
        <div className="slip" style={{ insetInlineStart: '8%', bottom: '14%' }}>
          <span className="mono">{t('home.room1_slip_label')}</span>
          {t('home.room1_slip')}
          <span className="caret" aria-hidden="true" />
        </div>
      ),
    },
    {
      title: t('home.room2_title'),
      desc: t('home.room2_desc'),
      fig: t('home.fig_room2'),
      img: roomChannels,
      alt: t('home.room2_alt'),
      fragment: (
        <div className="slip" style={{ insetInlineEnd: '8%', top: '12%' }}>
          <span className="mono">{t('home.room2_slip_label')}</span>
          <ul className="grid gap-2">
            {(['wa', 'sms', 'email'] as const).map((k) => (
              <li key={k} className="flex justify-between gap-4 border-t border-[var(--hairline)] pt-2">
                <span className="font-medium">{t(`home.room2_${k}`)}</span>
                <span className="mono" style={{ margin: 0 }}>{t(`home.room2_${k}_note`)}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      title: t('home.room3_title'),
      desc: t('home.room3_desc'),
      fig: t('home.fig_room3'),
      img: roomAnalytics,
      alt: t('home.room3_alt'),
      fragment: (
        <div className="slip" style={{ insetInlineStart: '8%', top: '14%' }}>
          <span className="mono">{t('home.room3_slip_label')}</span>
          <div className="meter">
            {[
              { label: t('home.room3_delivered'), value: 96, cls: '' },
              { label: t('home.room3_read'), value: 71, cls: 'is-glass' },
              { label: t('home.room3_replied'), value: 18, cls: 'is-ember' },
            ].map((row) => (
              <div key={row.label} className="meter__row">
                <span>{row.label}</span>
                <span className="meter__bar"><i className={row.cls} style={{ width: `${row.value}%` }} /></span>
                <span className="mono" style={{ margin: 0 }}>{row.value}%</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <SceneLayout>
      {/* Scene 1 — establishing shot: the facade as portrait, the message as the figure */}
      <section className="hero">
        <Aperture ticks rv="aperture" label={t('home.coords')} className="hero__frame">
          <div className="hero__copy">
            <h1 className="display max-w-[12ch]">{t('home.hero_title')}</h1>
            <p className="body max-w-[44ch]">{t('home.hero_subtitle')}</p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
              <button type="button" className="btn-ink" onClick={() => navigate('/how-it-works#book-demo')}>
                {t('home.cta_demo')}
                <ArrowRight className="btn-arrow h-4 w-4" />
              </button>
              <button type="button" className="link-line" onClick={() => navigate('/pricing')}>
                {t('home.cta_pricing')}
              </button>
            </div>
          </div>
          <Poche tone="dusk" src={heroFacade} alt={t('home.hero_alt')} focus="50% 18%" eager className="hero__stage">
            <span className="hero__coords mono">A-101 · 1:200</span>
            <div className="bubble" style={{ insetInlineStart: '30%', top: '34%' }}>
              {t('home.bubble')}
              <span className="bubble__meta mono">
                <span>WhatsApp</span>
                <span className="bubble__ticks">19:42 ✓✓</span>
              </span>
            </div>
          </Poche>
        </Aperture>
      </section>

      {/* Scene 2 — still shot */}
      <section className="wrap" data-rv="dissolve">
        <div className="still">
          <p className="lede max-w-[24ch]">{t('home.still_line')}</p>
          <Poche src={stillStair} alt={t('home.still_alt')} className="still__detail" />
        </div>
      </section>

      {/* Scene 3 — walk-through: one still frame cutting between rooms */}
      <section className="wrap section">
        <p className="mono mb-4" data-rv="up">{t('home.walk_label')}</p>
        <h2 className="display-m max-w-[18ch] mb-6">{t('home.walk_title')}</h2>
        <div className="walk">
          <div>
            {rooms.map((room, i) => (
              // Index keys: titles change with language, and remounting would detach the
              // nodes useActiveIndex observes.
              <article
                key={i}
                ref={register(i)}
                className="walk__step"
                data-active={active === i}
              >
                <span className="mono">{stepOf(i + 1, rooms.length)}</span>
                <h3 className="display-m" style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)' }}>{room.title}</h3>
                <p className="body max-w-[40ch]">{room.desc}</p>
                <Aperture className="walk__inline">
                  <Poche src={room.img} alt={room.alt} caption={room.fig} className="absolute inset-0" />
                  {room.fragment}
                </Aperture>
              </article>
            ))}
          </div>
          <Aperture ticks className="walk__stage" label={stepOf(active + 1, rooms.length)} labelEnd>
            {rooms.map((room, i) => (
              <div key={i} className="walk__room" data-active={active === i} aria-hidden={active !== i}>
                <Poche src={room.img} alt={room.alt} caption={room.fig} className="absolute inset-0" />
                {room.fragment}
              </div>
            ))}
            <div className="walk__progress" aria-hidden="true">
              {rooms.map((_, i) => <i key={i} data-on={i <= active} />)}
            </div>
          </Aperture>
        </div>
      </section>

      {/* Scene 4 — schedule of channels */}
      <section className="wrap section">
        <div className="split-golden">
          <div className="sticky-col grid gap-5">
            <p className="mono">S-01 · {t('home.sched_label')}</p>
            <h2 className="display-m">{t('home.sched_title')}</h2>
            <p className="body max-w-[36ch]">{t('home.sched_sub')}</p>
          </div>
          <div data-rv="line">
            <div className="ledger__head" />
            {SCHEDULE.map((key, i) => (
              <div key={key} className="ledger__row">
                <span className="mono">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-[17px] font-medium">{t(`home.sched.${key}.name`)}</span>
                <span className="body text-[15px]">{t(`home.sched.${key}.desc`)}</span>
                <span className="mono" style={{ color: key === 'wa' ? 'var(--glass)' : undefined }}>
                  {t(`home.sched.${key}.tag`)}
                </span>
                <span className="rv-line" style={{ ['--i' as string]: i }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scene 5 — the invitation, inside a doorway */}
      <section className="wrap section">
        <Aperture ticks className="door" rv="rack" label={t('home.invite_label')}>
          <Poche src={transom} className="door__transom" />
          <div className="door__body">
            <h2 className="display-m">{t('home.invite_title')}</h2>
            <p className="body">{t('home.invite_sub')}</p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
              <button type="button" className="btn-ink" onClick={() => navigate('/how-it-works#book-demo')}>
                {t('home.invite_cta')}
                <ArrowRight className="btn-arrow h-4 w-4" />
              </button>
              <button type="button" className="link-line" onClick={() => navigate('/pricing')}>
                {t('home.cta_pricing')}
              </button>
            </div>
          </div>
        </Aperture>
      </section>
    </SceneLayout>
  );
}
